import { normalizeCoachDow, mon0ToSun0 } from './weekdays';
import { normalizeProgramDayType } from './programDayType';

// Best-effort extraction from coach editor objects
function extractRawDow(p: any): unknown {
  return (
    p?.sun0 ??
    p?.weekdaySun0 ??
    p?.weekday ??
    p?.dayOfWeek ??
    p?.day_of_week ??
    p?.weekdayMon0 ??
    p?.weekdayMon1 ??
    p?.dayName ??
    p?.day ??
    null
  );
}

/**
 * Ensure a complete 7-slot Sun->Sat pattern.
 * If a row has no explicit day, fall back to its index interpreted as Mon=0.
 * Last one wins for duplicates (explicit > inferred).
 */
export function ensureSun0Pattern(pattern: any[]): Array<any> {
  const norm = pattern.map((p, i) => {
    const raw = extractRawDow(p);
    let sun0: number | null = normalizeCoachDow(raw, { alreadySun0: p?.alreadySun0 === true });
    if (sun0 == null) {
      // Fallback: treat index as Mon=0..Sun=6
      sun0 = mon0ToSun0(i);
    }
    // Freeform-first: if a coach typed a title, prefer that to infer non-rest
    const type = normalizeProgramDayType((p?.type ?? p?.title ?? '') as string);
    return {
      ...p,
      weekday: sun0 as 0 | 1 | 2 | 3 | 4 | 5 | 6,
      type,
      title: p?.title ?? (type === 'rest' ? 'Rest' : 'Workout'),
      durationMin: type === 'rest' ? undefined : p?.durationMin,
      meals: p?.meals ?? 4,
    };
  });

  const slots: Array<any> = new Array(7);
  for (const p of norm) {
    slots[p.weekday] = p;
  }
  for (let d = 0; d < 7; d++) {
    if (!slots[d]) {
      slots[d] = { weekday: d, type: 'rest', title: 'Rest', meals: 4 };
    }
  }
  return slots;
}

// Turn an arbitrary pattern into a complete 7-slot Sun0 array; empty input => 7 Rest days.
export function toSun0Week(pattern: any[] | undefined): Array<any> {
  const slots: Array<any> = new Array(7);
  if (Array.isArray(pattern)) {
    for (let i = 0; i < pattern.length; i++) {
      const p = pattern[i] ?? {};
      let sun0 = normalizeCoachDow(extractRawDow(p), { alreadySun0: p.alreadySun0 === true });
      if (sun0 == null) sun0 = mon0ToSun0(i);
      const t = normalizeProgramDayType(p.type);
      slots[sun0 as 0 | 1 | 2 | 3 | 4 | 5 | 6] = {
        ...p,
        weekday: sun0 as 0 | 1 | 2 | 3 | 4 | 5 | 6,
        type: t,
        title: p.title ?? (t === 'rest' ? 'Rest' : 'Workout'),
        durationMin: t === 'rest' ? undefined : p.durationMin,
        meals: p.meals ?? 4,
        // carry inline exercises through pattern normalization if present
        exercises: Array.isArray((p as any).exercises) ? (p as any).exercises : undefined,
      };
    }
  }
  for (let d = 0; d < 7; d++) {
    if (!slots[d]) slots[d] = { weekday: d as 0 | 1 | 2 | 3 | 4 | 5 | 6, type: 'rest', title: 'Rest', meals: 4 };
  }
  return slots;
}
